/**
 * A file size a person can read, and the sentence an upload in flight needs.
 *
 * <p>The Content Library used to print every size in KB, which is fine for a worksheet
 * and useless for the case that actually matters: a lesson video arrived as
 * "524,288 KB". Now that an upload shows its progress, the same number is also being
 * read while it moves, so it has to be short enough to change twice a second without
 * the row jumping.
 *
 * <p>Pure and dependency-free, so the rounding can be run rather than eyeballed.
 */

const UNITS = ['bytes', 'KB', 'MB', 'GB'];

/**
 * @param bytes a size, or anything that is not one
 * @returns e.g. "512 MB", "1.4 MB", "900 bytes", or null when there is no size to show
 *
 * Decimal units, not binary: this is the number a browser's download panel and a
 * file manager both show, and matching them matters more than matching the divisor
 * the server counted with. 1 KB is 1000 bytes here.
 */
export function formatBytes(bytes) {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1000) return `${Math.round(bytes)} ${UNITS[0]}`;

  let value = bytes;
  let unit = 0;
  // Stops at GB rather than carrying on: nothing here is allowed past 512 MB, and a
  // size in TB would be a bug worth seeing as an enormous number of GB.
  while (value >= 1000 && unit < UNITS.length - 1) {
    value /= 1000;
    unit += 1;
  }
  // One decimal below 10 and none above, so the width barely moves as it counts up:
  // "9.7 MB" then "12 MB", never "9.7 MB" then "12.4 MB".
  const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
  return `${rounded} ${UNITS[unit]}`;
}

/**
 * How far along an upload is, as a whole percent, or null when the browser could not
 * say how big the body was. A body of unknown length is reported by XMLHttpRequest with
 * lengthComputable false, and a bar with no total is a bar stuck at zero.
 */
export function uploadPercent({ loaded, total } = {}) {
  if (typeof loaded !== 'number' || typeof total !== 'number' || total <= 0) return null;
  // Clamped: a multipart body is slightly larger than the file, and a browser that
  // counts the envelope against a total that did not can report more than 100.
  return Math.min(100, Math.max(0, Math.floor((loaded / total) * 100)));
}
